import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { THEMES, FONTS, applyThemeSettings } from '../../components/layout/ThemeSync';
import { CircleCheck, CircleX, Gem, Handshake, Image, LoaderCircle, Palette, Save, Tag, Type, X, Zap } from 'lucide-react';

export default function DesignSettingsPanel() {
  const [themeColor, setThemeColor] = useState<keyof typeof THEMES>('amber');
  const [fontFamily, setFontFamily] = useState<keyof typeof FONTS>('sans-serif');
  const [brandName, setBrandName] = useState<string>('COBRAND');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  // 이미지 교환을 위한 타임스탬프 및 업로드 상태 관리
  const [heroTimestamp, setHeroTimestamp] = useState(Date.now());
  const [brandHeroTimestamp, setBrandHeroTimestamp] = useState(Date.now());
  
  const [whyDesignTs, setWhyDesignTs] = useState(Date.now());
  const [whyProductionTs, setWhyProductionTs] = useState(Date.now());
  const [whyMaterialTs, setWhyMaterialTs] = useState(Date.now());
  
  const [partnerATs, setPartnerATs] = useState(Date.now());
  const [partnerBTs, setPartnerBTs] = useState(Date.now());
  const [partnerCTs, setPartnerCTs] = useState(Date.now());
  const [partnerDTs, setPartnerDTs] = useState(Date.now());

  const [value1Ts, setValue1Ts] = useState(Date.now());
  const [value2Ts, setValue2Ts] = useState(Date.now());
  const [value3Ts, setValue3Ts] = useState(Date.now());
  const [value4Ts, setValue4Ts] = useState(Date.now());

  const [uploadingTarget, setUploadingTarget] = useState<string | null>(null);

  // 이미지 업로드 요청 처리
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>, target: string) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingTarget(target);
    setMessage(null);

    const formData = new FormData();
    formData.append('image', file);
    formData.append('target', target);

    const token = localStorage.getItem('cobrand_token');
    axios.post('/api/settings/upload-image', formData, {
      headers: { 
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${token}` 
      }
    })
      .then(() => {
        const now = Date.now();
        if (target === 'hero') setHeroTimestamp(now);
        else if (target === 'brand_hero') setBrandHeroTimestamp(now);
        else if (target === 'why_design') setWhyDesignTs(now);
        else if (target === 'why_production') setWhyProductionTs(now);
        else if (target === 'why_material') setWhyMaterialTs(now);
        else if (target === 'partner_A') setPartnerATs(now);
        else if (target === 'partner_B') setPartnerBTs(now);
        else if (target === 'partner_C') setPartnerCTs(now);
        else if (target === 'partner_D') setPartnerDTs(now);
        else if (target === 'value_1') setValue1Ts(now);
        else if (target === 'value_2') setValue2Ts(now);
        else if (target === 'value_3') setValue3Ts(now);
        else if (target === 'value_4') setValue4Ts(now);

        setMessage({ type: 'success', text: '배경 이미지가 성공적으로 변경되었습니다.' });
      })
      .catch(err => {
        const errText = err.response?.data?.error ?? '이미지 업로드에 실패했습니다.';
        setMessage({ type: 'error', text: errText });
      })
      .finally(() => {
        setUploadingTarget(null);
        // input element reset
        e.target.value = '';
      });
  };

  // 현재 설정 불러오기
  useEffect(() => {
    axios.get('/api/settings')
      .then(res => {
        const { theme_color, font_family, brand_name } = res.data;
        if (theme_color) setThemeColor(theme_color);
        if (font_family) setFontFamily(font_family);
        if (brand_name) setBrandName(brand_name);
      })
      .catch(() => {
        setMessage({ type: 'error', text: '설정을 로드하는 중 오류가 발생했습니다.' });
      });
  }, []);

  // 설정 저장하기
  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    const token = localStorage.getItem('cobrand_token');
    axios.post('/api/settings', {
      theme_color: themeColor,
      font_family: fontFamily,
      brand_name: brandName
    }, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(() => {
        // 성공 시 즉시 로컬 캐시 갱신 및 스타일 시트 적용
        localStorage.setItem('site_theme_color', themeColor);
        localStorage.setItem('site_font_family', fontFamily);
        localStorage.setItem('site_brand_name', brandName);
        applyThemeSettings(themeColor, fontFamily);
        window.dispatchEvent(new Event('site_settings_updated'));

        setMessage({ type: 'success', text: '사이트 설정이 저장되어 전체 사이트에 반영되었습니다.' });
      })
      .catch(err => {
        const errText = err.response?.data?.error ?? '설정을 저장하지 못했습니다.';
        setMessage({ type: 'error', text: errText });
      })
      .finally(() => {
        setSaving(false);
      });
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <Helmet>
        <title>사이트 설정 - COBRAND 관리자</title>
      </Helmet>

      {/* 헤더 */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">사이트 테마 및 디자인 설정</h1>
          <p className="text-stone-500 text-sm mt-1">사이트 전체(공개 웹페이지 및 관리자 화면)의 메인 칼라 톤과 폰트 스타일을 관리합니다.</p>
        </div>
      </div>

      {message && (
        <div className={`p-4 rounded-xl mb-6 flex items-center justify-between border ${
          message.type === 'success' 
            ? 'bg-emerald-50 border-emerald-200 text-emerald-800' 
            : 'bg-rose-50 border-rose-200 text-rose-800'
        }`}>
          <div className="flex items-center gap-3">
            {message.type === 'success' ? <CircleCheck size={18} /> : <CircleX size={18} />}
            <p className="text-sm font-medium">{message.text}</p>
          </div>
          <button onClick={() => setMessage(null)} className="icon-button text-stone-400 hover:text-stone-600" aria-label="알림 닫기"><X size={17} /></button>
        </div>
      )}

      <form onSubmit={handleSave} className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* 설정 변경 영역 */}
        <div className="md:col-span-2 space-y-6">
          
          {/* 브랜드 이름 설정 */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-stone-200">
            <h2 className="text-lg font-bold text-stone-900 mb-4 flex items-center gap-2">
              <Tag size={19} /> 브랜드 이름 설정
            </h2>
            <div>
              <label className="block text-xs font-semibold text-stone-500 mb-2">헤더/푸터 및 관리자 사이드바 로고에 표시될 텍스트</label>
              <input
                type="text"
                value={brandName}
                onChange={e => setBrandName(e.target.value)}
                maxLength={50}
                required
                className="w-full border border-stone-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm font-bold text-stone-800"
                placeholder="예: COBRAND"
              />
            </div>
          </div>
          
          {/* 웹페이지 대표 이미지 설정 */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-stone-200">
            <h2 className="text-lg font-bold text-stone-900 mb-4 flex items-center gap-2">
              <Image size={19} /> 메인 배경 이미지 교환
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              
              {/* HOME 메인 배경 */}
              <div className="border border-stone-100 rounded-xl p-4 bg-stone-50 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-bold text-stone-800">HOME 메인 히어로 배경</p>
                  {uploadingTarget === 'hero' && <span className="text-[10px] text-amber-600 font-bold animate-pulse">업로드 중...</span>}
                </div>
                <div className="aspect-video w-full rounded-lg overflow-hidden bg-stone-200 relative border border-stone-200">
                  <img src={`/hero.webp?t=${heroTimestamp}`} alt="HOME 메인" className="w-full h-full object-cover" />
                  {uploadingTarget === 'hero' && (
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                </div>
                <label className="block">
                  <span className="sr-only">이미지 선택</span>
                  <input
                    type="file"
                    accept="image/*"
                    disabled={uploadingTarget !== null}
                    onChange={e => handleImageUpload(e, 'hero')}
                    className="block w-full text-xs text-stone-500
                      file:mr-3 file:py-1.5 file:px-3
                      file:rounded-full file:border-0
                      file:text-xs file:font-semibold
                      file:bg-stone-200 file:text-stone-700
                      hover:file:bg-stone-300 cursor-pointer"
                  />
                </label>
              </div>

              {/* 브랜드 소개 배경 */}
              <div className="border border-stone-100 rounded-xl p-4 bg-stone-50 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-bold text-stone-800">브랜드 소개 히어로 배경</p>
                  {uploadingTarget === 'brand_hero' && <span className="text-[10px] text-amber-600 font-bold animate-pulse">업로드 중...</span>}
                </div>
                <div className="aspect-video w-full rounded-lg overflow-hidden bg-stone-200 relative border border-stone-200">
                  <img src={`/brand_hero.webp?t=${brandHeroTimestamp}`} alt="브랜드 히어로" className="w-full h-full object-cover" />
                  {uploadingTarget === 'brand_hero' && (
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                </div>
                <label className="block">
                  <span className="sr-only">이미지 선택</span>
                  <input
                    type="file"
                    accept="image/*"
                    disabled={uploadingTarget !== null}
                    onChange={e => handleImageUpload(e, 'brand_hero')}
                    className="block w-full text-xs text-stone-500
                      file:mr-3 file:py-1.5 file:px-3
                      file:rounded-full file:border-0
                      file:text-xs file:font-semibold
                      file:bg-stone-200 file:text-stone-700
                      hover:file:bg-stone-300 cursor-pointer"
                  />
                </label>
              </div>

            </div>
          </div>

          {/* ONDAM 강점 이미지 설정 */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-stone-200">
            <h2 className="text-lg font-bold text-stone-900 mb-4 flex items-center gap-2">
              <Zap size={19} /> ONDAM 강점 이미지 변경 (Why Choose Us)
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { label: '디자인 (Design)', target: 'why_design', ts: whyDesignTs },
                { label: '생산 (Production)', target: 'why_production', ts: whyProductionTs },
                { label: '재료 (Material)', target: 'why_material', ts: whyMaterialTs },
              ].map(item => (
                <div key={item.target} className="border border-stone-100 rounded-xl p-3 bg-stone-50 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-stone-700">{item.label}</span>
                    {uploadingTarget === item.target && <span className="text-[9px] text-amber-600 font-bold animate-pulse">중...</span>}
                  </div>
                  <div className="aspect-square w-full rounded-lg overflow-hidden bg-stone-200 relative border border-stone-200">
                    <img src={`/${item.target === 'why_design' ? 'design.webp' : item.target === 'why_production' ? 'production.webp' : 'material.webp'}?t=${item.ts}`} alt={item.label} className="w-full h-full object-cover" />
                    {uploadingTarget === item.target && (
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    )}
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    disabled={uploadingTarget !== null}
                    onChange={e => handleImageUpload(e, item.target)}
                    className="block w-full text-[10px] text-stone-500
                      file:mr-2 file:py-1 file:px-2.5
                      file:rounded-full file:border-0
                      file:text-[10px] file:font-semibold
                      file:bg-stone-200 file:text-stone-700
                      hover:file:bg-stone-300 cursor-pointer"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* 전문 파트너사 이미지 설정 */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-stone-200">
            <h2 className="text-lg font-bold text-stone-900 mb-4 flex items-center gap-2">
              <Handshake size={19} /> 전문 파트너사 이미지 변경 (Professional Alliance)
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { label: '파트너 A (디자인)', target: 'partner_A', ts: partnerATs },
                { label: '파트너 B (원자재)', target: 'partner_B', ts: partnerBTs },
                { label: '파트너 C (생산)', target: 'partner_C', ts: partnerCTs },
                { label: '파트너 D (유통)', target: 'partner_D', ts: partnerDTs },
              ].map(item => (
                <div key={item.target} className="border border-stone-100 rounded-xl p-3 bg-stone-50 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-bold text-stone-700">{item.label}</span>
                    {uploadingTarget === item.target && <span className="text-[9px] text-amber-600 font-bold animate-pulse">중...</span>}
                  </div>
                  <div className="aspect-square w-full rounded-lg overflow-hidden bg-stone-200 relative border border-stone-200">
                    <img src={`/${item.target}.webp?t=${item.ts}`} alt={item.label} className="w-full h-full object-cover" />
                    {uploadingTarget === item.target && (
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    )}
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    disabled={uploadingTarget !== null}
                    onChange={e => handleImageUpload(e, item.target)}
                    className="block w-full text-[10px] text-stone-500
                      file:mr-1 file:py-1 file:px-2
                      file:rounded-full file:border-0
                      file:text-[9px] file:font-semibold
                      file:bg-stone-200 file:text-stone-700
                      hover:file:bg-stone-300 cursor-pointer"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* 핵심 가치 이미지 설정 */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-stone-200">
            <h2 className="text-lg font-bold text-stone-900 mb-4 flex items-center gap-2">
              <Gem size={19} /> 핵심 가치 이미지 변경 (Core Values)
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { label: '가치 1 (전문성)', target: 'value_1', ts: value1Ts },
                { label: '가치 2 (신뢰)', target: 'value_2', ts: value2Ts },
                { label: '가치 3 (혁신)', target: 'value_3', ts: value3Ts },
                { label: '가치 4 (지속가능)', target: 'value_4', ts: value4Ts },
              ].map(item => (
                <div key={item.target} className="border border-stone-100 rounded-xl p-3 bg-stone-50 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-bold text-stone-700">{item.label}</span>
                    {uploadingTarget === item.target && <span className="text-[9px] text-amber-600 font-bold animate-pulse">중...</span>}
                  </div>
                  <div className="aspect-square w-full rounded-lg overflow-hidden bg-stone-200 relative border border-stone-200">
                    <img src={`/${item.target}.webp?t=${item.ts}`} alt={item.label} className="w-full h-full object-cover" />
                    {uploadingTarget === item.target && (
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    )}
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    disabled={uploadingTarget !== null}
                    onChange={e => handleImageUpload(e, item.target)}
                    className="block w-full text-[10px] text-stone-500
                      file:mr-1 file:py-1 file:px-2
                      file:rounded-full file:border-0
                      file:text-[9px] file:font-semibold
                      file:bg-stone-200 file:text-stone-700
                      hover:file:bg-stone-300 cursor-pointer"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* 테마 칼라 선택 */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-stone-200">
            <h2 className="text-lg font-bold text-stone-900 mb-4 flex items-center gap-2">
              <Palette size={19} /> 메인 테마 색상 선택
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[
                { id: 'amber', label: '골드 앰버 (Amber)', color: 'bg-amber-600' },
                { id: 'emerald', label: '에메랄드 그린 (Green)', color: 'bg-emerald-600' },
                { id: 'blue', label: '오션 블루 (Blue)', color: 'bg-blue-600' },
                { id: 'rose', label: '럭셔리 로즈 (Rose)', color: 'bg-rose-600' },
                { id: 'stone', label: '모던 스톤 (Stone)', color: 'bg-stone-600' },
                { id: 'indigo', label: '미드나잇 인디고 (Indigo)', color: 'bg-indigo-600' }
              ].map(theme => (
                <button
                  key={theme.id}
                  type="button"
                  onClick={() => setThemeColor(theme.id as any)}
                  className={`flex flex-col items-center justify-center p-4 rounded-xl border-2 transition ${
                    themeColor === theme.id 
                      ? 'border-amber-600 bg-amber-50/20' 
                      : 'border-stone-100 bg-stone-50 hover:bg-stone-100'
                  }`}
                >
                  <span className={`w-8 h-8 rounded-full ${theme.color} shadow-inner mb-2`} />
                  <span className="text-xs font-semibold text-stone-700">{theme.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* 폰트 선택 */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-stone-200">
            <h2 className="text-lg font-bold text-stone-900 mb-4 flex items-center gap-2">
              <Type size={19} /> 폰트 스타일 선택
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { id: 'sans-serif', label: '시스템 고딕 (Default)', desc: '빠르고 표준적인 기본 폰트', style: { fontFamily: FONTS['sans-serif'] } },
                { id: 'Inter', label: 'Inter (Modern Sans)', desc: '세련된 영문 중심의 모던 폰트', style: { fontFamily: FONTS.Inter } },
                { id: 'Outfit', label: 'Outfit (Premium Rounded)', desc: '둥글고 고급스러운 디자인 폰트', style: { fontFamily: FONTS.Outfit } },
                { id: 'Playfair Display', label: 'Playfair Display (Serif)', desc: '우아한 명조 계열의 세리프 폰트', style: { fontFamily: FONTS['Playfair Display'] } },
                { id: 'Nanum Gothic', label: '나눔고딕 (Nanum Gothic)', desc: '가독성이 높은 한글 고딕 폰트', style: { fontFamily: FONTS['Nanum Gothic'] } },
                { id: 'Nanum Myeongjo', label: '나눔명조 (Nanum Myeongjo)', desc: '격식 있는 고풍스러운 명조 폰트', style: { fontFamily: FONTS['Nanum Myeongjo'] } }
              ].map(font => (
                <button
                  key={font.id}
                  type="button"
                  onClick={() => setFontFamily(font.id as any)}
                  className={`flex flex-col items-start p-4 rounded-xl border-2 transition text-left w-full ${
                    fontFamily === font.id 
                      ? 'border-amber-600 bg-amber-50/20' 
                      : 'border-stone-100 bg-stone-50 hover:bg-stone-100'
                  }`}
                  style={font.style}
                >
                  <span className="text-sm font-bold text-stone-900 mb-1">{font.label}</span>
                  <span className="text-xs text-stone-500 leading-relaxed">{font.desc}</span>
                </button>
              ))}
            </div>
          </div>

          {/* 저장 버튼 */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="bg-amber-500 hover:bg-amber-600 text-black font-bold px-8 py-3 rounded-xl transition flex items-center gap-2 shadow-sm disabled:opacity-50"
            >
              {saving ? <LoaderCircle size={17} className="animate-spin" /> : <Save size={17} />}
              <span>{saving ? '저장 중...' : '설정 일괄 저장'}</span>
            </button>
          </div>

        </div>

        {/* 우측 실시간 프리뷰 카드 */}
        <div className="space-y-6">
          <div className="bg-stone-900 rounded-2xl p-6 shadow-md text-white sticky top-6 border border-stone-800">
            <h3 className="text-sm font-semibold text-stone-400 uppercase tracking-widest mb-4">실시간 미리보기 (Preview)</h3>
            
            <div className="border border-stone-800 rounded-xl p-4 bg-stone-950/60 mb-6 space-y-4">
              <div>
                <p className="text-[10px] text-stone-500 uppercase font-medium">선택된 테마</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="w-3.5 h-3.5 rounded-full bg-amber-600" style={{ backgroundColor: THEMES[themeColor]['600'] }} />
                  <span className="text-xs font-bold text-stone-200 capitalize">{themeColor}</span>
                </div>
              </div>
              <div>
                <p className="text-[10px] text-stone-500 uppercase font-medium">선택된 폰트</p>
                <span className="text-xs font-bold text-stone-200 capitalize">{fontFamily}</span>
              </div>
              <div>
                <p className="text-[10px] text-stone-500 uppercase font-medium">브랜드 이름</p>
                <span className="text-xs font-bold text-stone-200">{brandName}</span>
              </div>
            </div>

            {/* 카드 프리뷰 */}
            <div className="border border-stone-800 rounded-xl p-5 bg-white text-stone-900 space-y-4" style={{ fontFamily: FONTS[fontFamily] }}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800" style={{
                  backgroundColor: `${THEMES[themeColor]['100']}dd`,
                  color: THEMES[themeColor]['900']
                }}>
                  BAG-001
                </span>
                <span className="text-xs text-stone-400">즉시 배송</span>
              </div>
              <div>
                <h4 className="font-bold text-sm text-stone-800">클래식 레더 토트백</h4>
                <p className="text-xs text-stone-500 mt-0.5">이태리 소가죽 소재 프리미엄 백</p>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-stone-100">
                <span className="font-extrabold text-amber-600" style={{ color: THEMES[themeColor]['600'] }}>185,000원</span>
                <button type="button" className="text-[10px] bg-amber-500 hover:bg-amber-600 text-black font-bold px-2.5 py-1.5 rounded-full" style={{
                  backgroundColor: THEMES[themeColor]['500'],
                  color: ['amber', 'emerald', 'blue', 'rose', 'indigo'].includes(themeColor) ? '#000000' : '#ffffff'
                }}>
                  제품 상세
                </button>
              </div>
            </div>

            <p className="text-[11px] text-stone-500 mt-4 leading-relaxed">
              * 프리뷰 카드의 폰트 및 핵심 포인트 컬러(태그, 버튼, 금액)는 현재 선택된 옵션의 상태를 보여줍니다.
            </p>
          </div>
        </div>
      </form>
    </div>
  );
}
